import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Select,
  Button,
  Spin,
  Switch,
  Alert,
  Card,
  Divider,
  Space,
  Empty
} from 'antd';
import {
  SaveOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  KeyOutlined,
  BarChartOutlined,
  PercentageOutlined,
} from '@ant-design/icons';
import {
  fetchRbacRoles,
  fetchActionsGrouped,
  fetchRolePermissions,
  bulkAssignPermissions,
  togglePermission
} from '../../redux/actions/rbacActions';

const { Option } = Select;

const PermissionsTab = () => {
  const dispatch = useDispatch();
  const { 
    roles, 
    actionsGrouped, 
    permissions,
    rolesLoading, 
    actionsLoading,
    permissionsLoading 
  } = useSelector(state => state.rbac);
  
  const [selectedRole, setSelectedRole] = useState(null);
  const [permissionStates, setPermissionStates] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    dispatch(fetchRbacRoles());
    dispatch(fetchActionsGrouped());
  }, [dispatch]);

  useEffect(() => {
    if (selectedRole) {
      dispatch(fetchRolePermissions(selectedRole));
    }
  }, [dispatch, selectedRole]);

  useEffect(() => {
    // Initialize permission states from fetched permissions
    if (permissions && permissions.length > 0) {
      const states = {};
      permissions.forEach(perm => {
        states[perm.action_id] = perm.enable;
      });
      setPermissionStates(states);
      setHasChanges(false);
    } else if (selectedRole) {
      // If no permissions exist for this role, set all to false
      setPermissionStates({});
      setHasChanges(false);
    }
  }, [permissions, selectedRole]);

  const handleRoleChange = (roleId) => {
    setSelectedRole(roleId);
    setPermissionStates({});
    setHasChanges(false);
  };

  const handlePermissionToggle = (actionId, enabled) => {
    setPermissionStates(prev => ({
      ...prev,
      [actionId]: enabled
    }));
    setHasChanges(true);
  };

  const handleGroupToggle = (groupActions, enabled) => {
    const newStates = { ...permissionStates };
    groupActions.forEach(action => {
      newStates[action.id] = enabled;
    });
    setPermissionStates(newStates);
    setHasChanges(true);
  };

  const handleSavePermissions = () => {
    if (!selectedRole) return;

    const actions = Object.entries(permissionStates).map(([actionId, enable]) => ({
      action_id: parseInt(actionId),
      enable: enable
    }));

    dispatch(bulkAssignPermissions({
      role_id: selectedRole,
      actions
    }));
    
    setHasChanges(false);
  };

  const handleRefresh = () => {
    if (selectedRole) {
      dispatch(fetchRolePermissions(selectedRole));
    }
    dispatch(fetchRbacRoles());
    dispatch(fetchActionsGrouped());
  };

  const selectedRoleData = roles.find(r => r.id === selectedRole);

  // Count enabled permissions
  const enabledCount = Object.values(permissionStates).filter(Boolean).length;
  const totalCount = actionsGrouped.reduce((sum, group) => sum + group.actions.length, 0);

  return (
    <div style={{ padding: '1rem' }}>
      {/* Stats Cards */}
      <div className="rbac-stats-grid">
        <div className="rbac-stat-card">
          <div className="rbac-stat-icon"><KeyOutlined /></div>
          <div className="rbac-stat-content">
            <div className="rbac-stat-value">{enabledCount}</div>
            <div className="rbac-stat-label">Permissions Assigned</div>
          </div>
        </div>
        <div className="rbac-stat-card" style={{ borderLeftColor: '#17a2b8' }}>
          <div className="rbac-stat-icon" style={{ background: '#d1ecf1', color: '#0c5460' }}><BarChartOutlined /></div>
          <div className="rbac-stat-content">
            <div className="rbac-stat-value">{totalCount}</div>
            <div className="rbac-stat-label">Total Available</div>
          </div>
        </div>
        <div className="rbac-stat-card" style={{ borderLeftColor: '#28a745' }}>
          <div className="rbac-stat-icon" style={{ background: '#d4edda', color: '#155724' }}><PercentageOutlined /></div>
          <div className="rbac-stat-content">
            <div className="rbac-stat-value">
              {totalCount > 0 ? Math.round((enabledCount / totalCount) * 100) : 0}%
            </div>
            <div className="rbac-stat-label">Coverage</div>
          </div>
        </div>
      </div>

      {/* Role Selection */}
      <Card style={{ marginTop: '1.5rem' }}>
        <div className="table-header" style={{ marginBottom: '1rem' }}>
          <h2 className="table-title">Manage Role Permissions</h2>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button className="btn btn-secondary" onClick={handleRefresh}>
              <ReloadOutlined /> Refresh
            </button>
            {hasChanges && (
              <button className="btn btn-primary" onClick={handleSavePermissions}>
                <SaveOutlined /> Save Changes
              </button>
            )}
          </div>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 500 }}>
            Select Role
          </label>
          <Select
            showSearch
            placeholder="Choose a role to manage permissions"
            style={{ width: '100%', maxWidth: 400 }}
            size="large"
            value={selectedRole}
            onChange={handleRoleChange}
            loading={rolesLoading}
            optionFilterProp="children"
          >
            {roles.map(role => (
              <Option key={role.id} value={role.id}>
                {role.role_name} {!role.enable && '(Disabled)'}
              </Option>
            ))}
          </Select>
        </div>

        {hasChanges && (
          <Alert
            message="You have unsaved changes"
            description="Don't forget to save your permission changes before switching roles or leaving this page."
            type="warning"
            showIcon
            style={{ marginBottom: '1rem' }}
          />
        )}

        {selectedRoleData && (
          <Alert
            message={`Managing permissions for: ${selectedRoleData.role_name}`}
            type="info"
            showIcon
            icon={<CheckCircleOutlined />}
            style={{ marginBottom: '1.5rem' }}
          />
        )}

        {permissionsLoading ? (
          <div style={{ textAlign: 'center', padding: '3rem' }}>
            <Spin size="large" tip="Loading permissions..." />
          </div>
        ) : !selectedRole ? (
          <Empty
            description="Select a role to manage its permissions"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ padding: '3rem 0' }}
          />
        ) : (
          <div className="permission-grid">
            {actionsGrouped.map(group => {
              const groupEnabled = group.actions.filter(
                action => permissionStates[action.id]
              ).length;
              const allEnabled = groupEnabled === group.actions.length;

              return (
                <div key={group.type} className="permission-group">
                  <div className="permission-group-header">
                    <div>
                      <span className="permission-group-title">{group.type}</span>
                      <span style={{ marginLeft: '0.5rem', color: '#6c757d', fontSize: '13px' }}>
                        ({groupEnabled}/{group.actions.length} enabled)
                      </span>
                    </div>
                    <Switch
                      checked={allEnabled}
                      onChange={(checked) => handleGroupToggle(group.actions, checked)}
                      checkedChildren="All On"
                      unCheckedChildren="All Off"
                    />
                  </div>

                  <div className="permission-items">
                    {group.actions.map(action => (
                      <div key={action.id} className="permission-item">
                        <div className="permission-item-info">
                          <div className="permission-item-name">{action.display_name}</div>
                          <div className="permission-item-action">{action.action}</div>
                        </div>
                        <Switch
                          checked={permissionStates[action.id] || false}
                          onChange={(checked) => handlePermissionToggle(action.id, checked)}
                          size="small"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {selectedRole && !permissionsLoading && actionsGrouped.length === 0 && (
          <Empty
            description="No actions available. Please create actions first."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ padding: '3rem 0' }}
          />
        )}
      </Card>
    </div>
  );
};

export default PermissionsTab;
